import React from "react";

export type MetricTone = "green" | "blue" | "purple" | "indigo" | "red" | "orange";

const TONES: Record<MetricTone, { border: string; glow: string; solid: string; ring: string; ringBg: string }> = {
  green: { border: "rgba(45,204,137,0.24)", glow: "rgba(45,204,137,0.45)", solid: "#07a66c", ring: "#00653b", ringBg: "#002c14" },
  blue: { border: "rgba(59,130,246,0.24)", glow: "rgba(59,130,246,0.45)", solid: "#3b82f6", ring: "#1d4ed8", ringBg: "#0b1a3a" },
  purple: { border: "rgba(168,85,247,0.24)", glow: "rgba(168,85,247,0.45)", solid: "#a855f7", ring: "#7e22ce", ringBg: "#24103a" },
  indigo: { border: "rgba(99,102,241,0.24)", glow: "rgba(99,102,241,0.45)", solid: "#6366f1", ring: "#4338ca", ringBg: "#16163f" },
  red: { border: "rgba(239,68,68,0.24)", glow: "rgba(239,68,68,0.45)", solid: "#ef4444", ring: "#b91c1c", ringBg: "#3a0d0d" },
  orange: { border: "rgba(249,115,22,0.24)", glow: "rgba(249,115,22,0.45)", solid: "#f97316", ring: "#c2410c", ringBg: "#3a1a06" },
};

interface MetricCardProps {
  tone: MetricTone;
  /** A lucide icon, drawn white inside the solid circle. */
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  /** sm: phone 2 × 2 grid; md: Trade cards (16 px value); lg: Statistics cards (28 px value). */
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * Stat card from the Trade and Statistics screens: solid icon circle inside a
 * fading ring, label above the value, coloured edge glow.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  tone,
  icon,
  label,
  value,
  size = "md",
  className = "",
}) => {
  const t = TONES[tone];
  const lg = size === "lg";
  const sm = size === "sm";
  return (
    <div
      className={`edge-glow relative flex min-w-0 items-center overflow-hidden rounded-xl border bg-[#0d121a] ${
        lg ? "gap-5 px-6 py-6" : sm ? "gap-2.5 px-3 py-3" : "gap-4 px-4 py-3"
      } ${className}`}
      style={{
        borderColor: t.border,
        ["--glow" as string]: t.glow,
        backgroundColor: "#0d121a",
        backgroundImage: `radial-gradient(ellipse 40% 7px at 50% 0, ${t.glow}, transparent), radial-gradient(ellipse 40% 7px at 50% 100%, ${t.glow}, transparent), linear-gradient(135deg, ${t.border.replace("0.24", lg ? "0.16" : "0.08")}, transparent 65%)`,
      }}
    >
      <span className={`relative flex shrink-0 items-center justify-center ${lg ? "size-14" : sm ? "size-10" : "size-12"}`}>
        <span
          className="absolute inset-0 rounded-full border"
          style={{
            borderColor: t.ring,
            background: t.ringBg,
            maskImage: "linear-gradient(to bottom, #000, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, #000, transparent)",
          }}
        />
        <span
          className={`relative flex items-center justify-center rounded-full text-white ${
            lg ? "size-9 [&>svg]:size-5" : sm ? "size-7 [&>svg]:size-4" : "size-8 [&>svg]:size-5"
          }`}
          style={{ background: t.solid }}
        >
          {icon}
        </span>
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 whitespace-nowrap">
        <span className={`truncate text-[#d4d4d4] ${lg ? "text-sm leading-5" : "text-xs leading-[18px]"}`}>
          {label}
        </span>
        <span
          className={`truncate font-semibold text-white ${lg ? "text-[28px] leading-9" : "text-base leading-6"}`}
        >
          {value}
        </span>
      </div>
    </div>
  );
};
